// NodeModal.jsx


import React, { useState } from "react";
import Modal from "../base/Modal"; // Adjust the import path as needed
import { MdArrowDropDown } from "react-icons/md";
import {
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationCircle,
  FaHourglassHalf,
  FaPauseCircle,
  FaStethoscope, // <-- Import this
  FaCommentDots
} from "react-icons/fa";
import { DataState } from "../../context/DataProvider";
import SplitsDisplay from "./SplitsDisplay"; // Import the SplitsDisplay component
import { fetchWithHandling, API_URL } from "../../services/api";
import toast from "react-hot-toast";
import ChatModal from "./ChatModal";
import OutputRenderer from "./OutputRenderer";

const MdBuild = ({ className }) => (
  <svg className={className} stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"></path></svg>
);

const NodeModal = ({ node, onClose, workflowId, executionId, workflowStatus }) => {
  const [open, setOpen] = useState(false);
  // Track approval decisions for each tool keyed by tool id.
  const [approvalDecisions, setApprovalDecisions] = useState({});
  const [loading, setLoading] = useState(false);

  const [isChatModalOpen, setIsChatModalOpen] = useState(false); // <-- Renamed for clarity

  const {
    userGoogleId,
  } = DataState();

  // Destructure node data. approvalNeededTools is expected in the form:
  // { id: { toolName, arguments } }
  let { label, status = "idle", output, error, approvalNeededTools, splits, improvement_notes_for_developer, chats } = node.data || {};
  // if (output === true) {
  //   output = "True"
  // } else if (output === false) {
  //   output = "False"
  // }
  const hasImprovementNotes =
    Array.isArray(improvement_notes_for_developer) &&
    improvement_notes_for_developer.length > 0;

  // Toggle details when node has either success or failure
  const toggleOpen = () => {
    if (status === "success" || status === "failed") setOpen((prev) => !prev);
  };

  // Icon rendering based on node status.
  const renderStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <FaHourglassHalf className="text-yellow-500" />;
      case "running":
        return <FaSpinner className="animate-spin text-[#5652CF]" />;
      case "success":
        return <FaCheckCircle className="text-green-500" />;
      case "failed":
        return <FaTimesCircle className="text-red-500" />;
      case "approvalNeeded":
        return <FaExclamationCircle className="text-yellow-500" />;
      case "diagnosing":
        return <FaStethoscope className="text-purple-500" />; // Customize color if needed
      case "skipped":
      case "idle":
      default:
        return <FaPauseCircle className="text-gray-500" />;
    }
  };

  // Return styling for status text.
  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-yellow-500";
      case "running":
        return "text-[#5652CF]";
      case "success":
        return "text-green-600";
      case "failed":
        return "text-red-500";
      case "approvalNeeded":
        return "text-yellow-500";
      default:
        return "text-gray-500";
    }
  };

  // ---------------------- Tool Approval Handlers ----------------------

  // Update decision for a given tool approval.
  const handleApprovalDecision = (toolId, approved, toolArguments) => {
    setApprovalDecisions((prev) => ({
      ...prev,
      [toolId]: {
        arguments: toolArguments,
        approved,
        disapproved: !approved,
        disapprovalReason: approved ? "" : (prev[toolId]?.disapprovalReason || ""),
      },
    }));
  };

  // Update the disapproval reason for a given tool approval.
  const handleDisapprovalReasonChange = (toolId, value) => {
    setApprovalDecisions((prev) => ({
      ...prev,
      [toolId]: {
        ...prev[toolId],
        disapprovalReason: value,
      },
    }));
  };

  // Validate that every tool approval decision is valid.
  const isApprovalReady = (approvalTools) => {
    return Object.entries(approvalTools).every(([toolId]) => {
      const decision = approvalDecisions[toolId];
      if (!decision) return false;
      if (decision.approved === true) return true;
      if (
        decision.approved === false &&
        decision.disapproved === true &&
        decision.disapprovalReason &&
        decision.disapprovalReason.trim() !== ""
      ) {
        return true;
      }
      return false;
    });
  };

  // Placeholder submit approval API call.
  const submitApproval = async () => {
    setLoading(true);
    try {
      // Replace the console log with your API integration.
      console.log("Submitting tool approval decisions:", approvalDecisions, executionId);
      const res = await fetchWithHandling(`${API_URL}/canvas/workflow/executions/${executionId}/nodes/${node.id}/approval-update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${userGoogleId}`
        },
        // The endpoint expects a key "approvalNeededEmails"
        body: JSON.stringify({ approvalNeededTools: approvalDecisions }),
      });
      // Reset decisions on successful submission.
      setApprovalDecisions({});
      toast.success("Tool approval submitted successfully!");
    } catch (error) {
      console.error("Error submitting approval:", error);
      toast.error(typeof error === "string" ? error : JSON.stringify(error))
    } finally {
      setLoading(false);
    }
  };

  // --------------------- Render Component ---------------------
  return (
    <Modal
      title={label || node.type || "Node"}
      desc="Node Details"
      isOpen={true}
      onClose={onClose}
    >
      <div className="p-6">
        {/* Node Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer rounded-md bg-gradient-to-r from-zinc-800 to-zinc-700 shadow-md"
          onClick={toggleOpen}
        >
          <div className="flex items-center gap-3">
            {renderStatusIcon(status)}
            <span className="font-semibold text-sm text-p-text">
              {label || node.type || "Node"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* UPDATED: Chat Toggle Button */}
            {node.type === 'action' && Array.isArray(chats) && chats.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); setIsChatModalOpen(true) }} // <-- This now opens the modal
                className="p-2 rounded-full transition-colors text-zinc-300 hover:text-white hover:bg-zinc-600"
                title="View Conversation"
              >
                <FaCommentDots />
              </button>
            )}
            <span className={`text-sm font-medium ${getStatusColor(status)}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
            {(status === "success" || status === "failed") && (
              <MdArrowDropDown
                className={`text-p-text transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"
                  }`}
              />
            )}

          </div>
        </div>

        {/* Output/Error Details Section */}
        {open && (status === "success" || status === "failed") && (
          <div className="mt-4 p-4 rounded-md border-t border-zinc-700 bg-zinc-800 text-p-text text-sm">
            {/* 2. Replace the old div with the new component */}
            <OutputRenderer content={output || error} />
          </div>
        )}
        {hasImprovementNotes && (
          <div className="mt-4 p-4 rounded-md border border-yellow-500/30 bg-yellow-500/10 text-white">
            <div className="flex items-center gap-2 mb-3">
              <MdBuild className="text-yellow-400 text-lg" />
              <h4 className="font-semibold text-yellow-400">Improvement Suggestions</h4>
            </div>
            <ul className="space-y-2 list-disc list-inside text-sm text-yellow-200/80">
              {improvement_notes_for_developer.map((note, index) => (<li key={index}>{note}</li>))}
            </ul>
          </div>
        )}

        {/* Splits Section */}
        {splits && splits.length > 0 && (
          <SplitsDisplay baseId={node.id} splits={splits} executionId={executionId} workflowId={workflowId} workflowStatus={workflowStatus}/>
        )}

        {/* Tool Approval Section */}
        {status === "approvalNeeded" &&
          approvalNeededTools &&
          Object.keys(approvalNeededTools).length > 0 && (
            <div className="mt-6 p-6 rounded-md bg-zinc-900 border border-zinc-700 shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-p-text">
                Tool Approval Required
              </h3>
              {Object.entries(approvalNeededTools).map(([toolId, toolData]) => {
                // toolData: { toolName, arguments (JSON string) }
                let parsedArgs = {};
                try {
                  parsedArgs = JSON.parse(toolData.arguments);
                } catch (err) {
                  console.error("Error parsing tool arguments", err);
                }
                const decision = approvalDecisions[toolId] || {};
                return (
                  <div
                    key={toolId}
                    className="mb-5 p-5 rounded-lg bg-gradient-to-br from-zinc-800 to-zinc-700 border border-zinc-600 shadow-sm"
                  >
                    <div className="mb-3">
                      <p className="font-bold text-base text-p-text">
                        {toolData.name}
                      </p>
                    </div>
                    <div className="mb-4">
                      <span className="block font-medium text-sm text-p-text">
                        Arguments:
                      </span>
                      <div className="mt-1 p-3 rounded bg-zinc-800 border border-zinc-600 text-xs text-p-text  whitespace-pre-wrap break-words ">
                        {JSON.stringify(parsedArgs, null, 2)}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() =>
                            handleApprovalDecision(toolId, true, toolData.arguments)
                          }
                          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${decision.approved === true
                            ? "bg-green-600 text-white"
                            : "bg-zinc-700 hover:bg-zinc-600 text-p-text"
                            }`}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() =>
                            handleApprovalDecision(toolId, false, toolData.arguments)
                          }
                          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${decision.approved === false
                            ? "bg-red-600 text-white"
                            : "bg-zinc-700 hover:bg-zinc-600 text-p-text"
                            }`}
                        >
                          Disapprove
                        </button>
                      </div>
                      {decision.approved === false && (
                        <input
                          type="text"
                          placeholder="Enter disapproval reason..."
                          value={decision.disapprovalReason || ""}
                          onChange={(e) =>
                            handleDisapprovalReasonChange(toolId, e.target.value)
                          }
                          className="w-full sm:w-auto flex-grow px-3 py-2 rounded-md border border-zinc-600 bg-zinc-800 text-p-text text-sm focus:outline-none focus:ring-2 focus:ring-[#5652CF] transition"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              {isApprovalReady(approvalNeededTools) && (
                <div className="mt-4 text-right">
                  <button
                    disabled={loading}
                    onClick={submitApproval}
                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-base font-medium transition-colors"
                  >
                    {loading ? "Submitting..." : "Submit Approval"}
                  </button>
                </div>
              )}
            </div>
          )}
        {/* NEW: Render ChatUI conditionally */}
        {/* NEW: Conditionally render the ChatModal outside the main modal's flow */}
        <ChatModal
          isOpen={isChatModalOpen}
          onClose={() => setIsChatModalOpen(false)}
          nodeLabel={label || node.type}
          chats={chats}
          nodeId={node.id}
          workflowId={workflowId}
          executionId={executionId}
          userGoogleId={userGoogleId}
          status={status}
          workflowStatus={workflowStatus}
        />
      </div>
    </Modal>
  );
};

export default NodeModal;
